import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { TrainingService } from '../../../shared/services/training/training.service';
import { catchError, map, of, switchMap } from 'rxjs';
import {
  clearSectionData,
  clearTrainingData,
  getCurrentSectionInfo,
  getCurrentSectionInfoFailed,
  getCurrentSectionInfoSuccess,
  getCurrentStage,
  getCurrentStageFailed,
  getCurrentStageSuccess,
  getSearchResultsFiles,
  getSearchResultsFilesFailed,
  getSearchResultsFilesSuccess,
  getSectionsList,
  getSectionsListFailed,
  getSectionsListSuccess,
} from '../actions/training.action';
import { selectFiltersState } from '../selectors/filters.selectors';
import { Store } from '@ngrx/store';
import { SectionsListModes } from '../../../shared/enums';
import { clearFiltersStore } from '../actions/filters.actions';

@Injectable()
export class TrainingEffects {
  private _actions$ = inject(Actions);
  private _trainingService = inject(TrainingService);
  private _store = inject(Store);

  public getSections$ = createEffect(() => {
    return this._actions$.pipe(
      ofType(getSectionsList),
      concatLatestFrom(() => [this._store.select(selectFiltersState)]),
      switchMap(([, filtersState]) => {
        if (filtersState.query && filtersState.query?.length) {
          const filters = {
            page: filtersState.page,
            size: filtersState.size,
            query: filtersState.query,
          };
          return of(getSearchResultsFiles({ filters }));
        }
        return this._trainingService.getSections().pipe(
          map((sectionsList) => {
            return getSectionsListSuccess({
              sectionsList: sectionsList,
            });
          }),
          catchError((error) => {
            return of(getSectionsListFailed({ error }));
          }),
        );
      }),
    );
  });

  public getSearchResultsFiles$ = createEffect(() => {
    return this._actions$.pipe(
      ofType(getSearchResultsFiles),
      switchMap((action) => {
        return this._trainingService.getTrainingFilesList(action.filters).pipe(
          map((filesList) => {
            return getSearchResultsFilesSuccess({
              trainingFilesList: filesList.elements,
              currentMode: SectionsListModes.searchMode,
              resetData: action.filters.page === 0,
            });
          }),
          catchError((error) => {
            return of(getSearchResultsFilesFailed({ error }));
          }),
        );
      }),
    );
  });

  public getCurrentSectionInfo$ = createEffect(() => {
    return this._actions$.pipe(
      ofType(getCurrentSectionInfo),
      switchMap((action) => {
        return this._trainingService.getCurrentSectionInfo(action.sectionId).pipe(
          map((section) => {
            return getCurrentSectionInfoSuccess({
              section: section,
            });
          }),
          catchError((error) => {
            return of(getCurrentSectionInfoFailed({ error }));
          }),
        );
      }),
    );
  });

  public getCurrentStage$ = createEffect(() => {
    return this._actions$.pipe(
      ofType(getCurrentStage),
      switchMap((action) => {
        return this._trainingService.getCurrentStage(action.stageId).pipe(
          map((stage) => {
            return getCurrentStageSuccess({
              stage: stage,
            });
          }),
          catchError((error) => {
            return of(getCurrentStageFailed({ error }));
          }),
        );
      }),
    );
  });

  public clearSectionData$ = createEffect(() => {
    return this._actions$.pipe(
      ofType(clearSectionData, clearTrainingData),
      switchMap(() => of(clearFiltersStore())),
    );
  });
}
